import { useNavigate } from '@tunasong/ui-lib'
import { useState } from 'react'

interface RouterHistoryState {
  idx: number
}

function isRouterHistoryState(state: unknown): state is RouterHistoryState {
  return typeof state === 'object' && state !== null && typeof (state as RouterHistoryState).idx === 'number'
}

/** React Router's browser history stores each entry's position in the session history as `idx` */
const getHistoryIndex = () => {
  const state: unknown = window.history.state
  return isRouterHistoryState(state) ? state.idx : null
}

/**
 * Exit back to the history entry that was current before this component mounted, so the browser history is the same
 * as before entering (e.g., performance mode). Entries pushed while mounted are left as forward history.
 * Calls `fallback` when there is no in-app entry to return to, e.g., when the app was opened directly on this view.
 */
export const useExitToEntryPoint = (fallback: () => void) => {
  const navigate = useNavigate()
  const [entryIndex] = useState(getHistoryIndex)

  return () => {
    const currentIndex = getHistoryIndex()
    if (entryIndex === null || currentIndex === null || entryIndex < 1) {
      fallback()
      return
    }
    void navigate(entryIndex - 1 - currentIndex)
  }
}
